import React, { useState } from "react";

import { mdiPlusCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import { BUTTON_VARIANTS } from "@/src/components/button/Button";
import { getSelectClassNames } from "@/src/components/form/Select";
import Modal from "@/src/components/modal/Modal";

import "../../../../css/admin/platform.scss";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type { ModalActions } from "@/src/components/modal/Modal";

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-platform");

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Icon path={mdiPlusCircleOutline} size={1.25} />
      <div className="shrink-0 grow basis-0 text-[1.171875rem] font-semibold">
        {t("addPlatformTitle")}
      </div>
    </div>
  );
}

export interface PlatformOption {
  value: string;
  label: string;
}

interface NewPlatformModalProps {
  currentPlatforms?: string[];
  supportedPlatforms?: string[];
  isOpen: boolean;
  onSubmit: (
    platform: PlatformOption,
    duplicate: boolean,
    platformDuplication: PlatformOption | null,
  ) => void;
  onClose: () => void;
}

function NewPlatformModal({
  currentPlatforms = [],
  supportedPlatforms = [],
  isOpen,
  onSubmit,
  onClose,
}: NewPlatformModalProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const [newPlatform, setNewPlatform] = useState<PlatformOption | null>(null);
  const [duplicatePlatform, setDuplicatePlatform] =
    useState<PlatformOption | null>(null);
  const [enableDuplication, setEnableDuplication] = useState(false);

  const getNewPlatformOptions = (): PlatformOption[] =>
    supportedPlatforms
      .filter((platform) => !currentPlatforms.includes(platform))
      .map((platform) => ({
        value: platform,
        label: t(platform),
      }));

  const getDuplicatePlatformOptions = (): PlatformOption[] =>
    currentPlatforms.map((platform) => ({
      value: platform,
      label: t(platform),
    }));

  const closeModal = (): void => {
    setNewPlatform(null);
    setDuplicatePlatform(null);
    setEnableDuplication(false);

    onClose();
  };

  const submit = (): void => {
    if (!newPlatform || (enableDuplication && !duplicatePlatform)) return;

    onSubmit(newPlatform, enableDuplication, duplicatePlatform);

    setNewPlatform(null);
    setDuplicatePlatform(null);
    setEnableDuplication(false);
  };

  const selectClassNames = getSelectClassNames();

  const modalActions: ModalActions[] = [
    {
      label: t("cancel"),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
      isCloseAfterAction: true,
      onClick: closeModal,
    },
    {
      label: t("confirm"),
      buttonVariant: BUTTON_VARIANTS.PRIMARY,
      isFullWidth: true,
      isCloseAfterAction: false,
      onClick: submit,
    },
  ];

  return (
    <Modal
      actions={modalActions}
      header={<ModalHeader />}
      id="new-platform-modal"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <div className="editor-form-section">
        <div className="editor-form-section-title">{t("selectPlatform")}</div>
        <div className="editor-form-input">
          <Select
            closeMenuOnSelect
            classNamePrefix="slct"
            classNames={selectClassNames}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            name="form-field-name"
            options={getNewPlatformOptions()}
            placeholder={t("select")}
            value={newPlatform}
            onChange={(option: SingleValue<PlatformOption>) =>
              setNewPlatform(option)
            }
          />
        </div>
      </div>

      <div className="flex gap-3">
        <input
          checked={enableDuplication}
          className="signup-term-checkbox"
          id="duplicate"
          name="duplicate"
          onChange={(e) => setEnableDuplication(e.target.checked)}
          type="checkbox"
        />
        <label className="signup-term" htmlFor="duplicate">
          {t("duplicate")}
        </label>
      </div>

      {enableDuplication ? (
        <div className="editor-form-section">
          <div className="editor-form-section-title">{t("selectModel")}</div>
          <div className="editor-form-input" style={{ minWidth: "300px" }}>
            <Select
              closeMenuOnSelect
              classNamePrefix="slct"
              classNames={selectClassNames}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              name="form-field-name"
              options={getDuplicatePlatformOptions()}
              placeholder={t("select")}
              value={duplicatePlatform}
              onChange={(option: SingleValue<PlatformOption>) =>
                setDuplicatePlatform(option)
              }
            />
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

export default NewPlatformModal;
